<template>
  <v-card rounded="md" class="ma-4">
    <v-card-title class="d-flex align-center justify-space-between">
      <span>
        <v-icon class="mr-2">mdi-shield-account</v-icon>
        {{ t('startTask.Beneficial Ownership Update E-Filing') || 'Beneficial Ownership Update - E-Filing' }}
      </span>
      <div class="d-flex align-center gap-2">
        <EfilingWalkthroughButton />
        <v-chip v-if="readOnly" color="success" variant="flat" size="small">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ t('startTask.Completed') || 'Completed' }}
        </v-chip>
        <!-- Test Mode toggle (non-production only) -->
        <v-chip
          v-if="!readOnly && eFiling.canUseTestMode"
          :color="eFiling.testMode.value ? 'warning' : 'grey-lighten-1'"
          :variant="eFiling.testMode.value ? 'flat' : 'outlined'"
          size="small"
          class="cursor-pointer"
          @click="eFiling.testMode.value = !eFiling.testMode.value"
        >
          <v-icon start size="small">mdi-test-tube</v-icon>
          {{ eFiling.testMode.value ? 'Test Mode ON' : 'Test Mode' }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider />

    <v-card-text>
      <!-- Test Mode Warning -->
      <v-alert
        v-if="!readOnly && eFiling.testMode.value"
        type="warning"
        variant="tonal"
        class="mb-4"
        density="compact"
        border="start"
      >
        <div class="d-flex align-center gap-2">
          <v-icon size="18">mdi-test-tube</v-icon>
          <span class="font-weight-medium">Test Mode ON</span>
          <span class="text-caption"> - No API calls will be made. Only validates form data.</span>
          <v-spacer />
          <v-btn
            size="small"
            variant="tonal"
            color="warning"
            @click="loadGroupTestData"
          >
            <v-icon start size="small">mdi-database-import</v-icon>
            Load Test Data (2 BO + Group)
          </v-btn>
        </div>
      </v-alert>

      <v-row>
        <!-- Left Column: Form -->
        <v-col cols="12" :md="pendingBOs.length > 0 ? 8 : 12">
          <!-- Tabs: Add BO / Group Arrangement -->
          <v-tabs v-model="activeTab" color="primary" class="mb-4">
            <v-tab value="addBO">
              <v-icon start>mdi-account-plus</v-icon>
              {{ t('startTask.Add Beneficial Owner') || 'Add Beneficial Owner' }}
              <v-chip v-if="pendingBOs.length > 0" size="x-small" color="success" class="ml-2">
                {{ pendingBOs.length }}
              </v-chip>
            </v-tab>
            <v-tab value="group" :disabled="eligibleBOCount < 2">
              <v-icon start>mdi-account-group</v-icon>
              {{ t('startTask.Group Arrangement') || 'Group Arrangement' }}
              <v-chip
                v-if="eligibleBOCount < 2"
                size="x-small"
                color="warning"
                class="ml-2"
              >
                {{ eligibleBOCount }}/2 {{ t('startTask.eligible') || 'eligible' }}
              </v-chip>
              <v-chip v-else size="x-small" color="info" class="ml-2">{{ t('startTask.Optional') || 'Optional' }}</v-chip>
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- Tab 1: Add Beneficial Owner -->
            <v-window-item value="addBO">
              <BeneficialOwnerForm
                ref="boFormRef"
                :task-id="taskId"
                :read-only="readOnly"
                @update:formData="handleBoFormDataUpdate"
              />

              <!-- Add / Save BO button -->
              <div v-if="!readOnly" class="d-flex justify-end gap-2 mt-4">
                <v-btn
                  v-if="editingBOIndex !== null"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  @click="cancelEditBO"
                >
                  <v-icon start>mdi-close</v-icon>
                  {{ t('startTask.Cancel') || 'Cancel' }}
                </v-btn>
                <v-btn
                  :color="editingBOIndex !== null ? 'warning' : 'primary'"
                  variant="flat"
                  size="large"
                  @click="handleAddToList"
                >
                  <v-icon start>{{ editingBOIndex !== null ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                  {{ editingBOIndex !== null ? (t('startTask.Save Changes') || 'Save Changes') : (t('startTask.Add') || 'Add') }}
                </v-btn>
              </div>
            </v-window-item>

            <!-- Tab 2: Group Arrangement (Optional) -->
            <v-window-item value="group">
              <BeneficialOwnerGroupForm
                ref="bogFormRef"
                :registeredBOs="pendingBOsAsRegistered"
                :read-only="readOnly"
                @update:formData="handleGroupFormDataUpdate"
              />
            </v-window-item>
          </v-window>
        </v-col>

        <!-- Right Column: Pending BOs list (sticky sidebar) -->
        <v-col v-if="pendingBOs.length > 0" cols="12" md="4">
          <div class="bo-sidebar">
            <v-card variant="outlined" class="bo-sidebar-card">
              <v-card-title class="text-subtitle-2 d-flex align-center flex-wrap ga-1 py-2 px-3">
                <v-icon size="small" class="mr-1">mdi-account-multiple-plus</v-icon>
                {{ t('startTask.Added Beneficial Owners') || 'Added BOs' }} ({{ pendingBOs.length }})
                <v-chip
                  v-if="eligibleBOCount > 0"
                  size="x-small"
                  color="success"
                  variant="tonal"
                >
                  <v-icon start size="x-small">mdi-account-group</v-icon>
                  {{ eligibleBOCount }}
                </v-chip>
                <v-chip
                  v-if="editingBOIndex !== null"
                  size="x-small"
                  color="warning"
                  variant="flat"
                >
                  <v-icon start size="x-small">mdi-pencil</v-icon>
                  #{{ editingBOIndex + 1 }}
                </v-chip>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-0">
                <v-list density="compact" class="pa-0">
                  <v-list-item
                    v-for="(bo, idx) in pendingBOs"
                    :key="idx"
                    :class="{
                      'bg-warning-lighten-5': editingBOIndex === idx,
                      'bg-primary-lighten-5 bo-list-item--selected': readOnly && selectedBOIndex === idx,
                    }"
                    :style="readOnly ? 'cursor: pointer' : ''"
                    class="bo-list-item"
                    @click="readOnly ? viewPendingBO(idx) : undefined"
                  >
                    <template #prepend>
                      <v-avatar size="28" :color="editingBOIndex === idx ? 'warning' : 'primary'" variant="tonal" class="text-caption font-weight-bold">
                        {{ idx + 1 }}
                      </v-avatar>
                    </template>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ bo.name || '-' }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ bo.idNo || '-' }} &middot;
                      {{ bo.typeBo === 'C' ? 'CTRL' : bo.typeBo === 'D' ? 'DIRECT' : bo.typeBo === 'I' ? 'INDIRECT' : bo.typeBo === 'DI' ? 'D&I' : '-' }}
                      <v-icon v-if="isEligibleForGroup(bo)" color="success" size="x-small" class="ml-1">mdi-account-group</v-icon>
                    </v-list-item-subtitle>
                    <template v-if="!readOnly" #append>
                      <v-btn
                        icon="mdi-pencil"
                        size="x-small"
                        variant="text"
                        color="primary"
                        density="compact"
                        :disabled="eFiling.isSubmitting.value"
                        @click="editPendingBO(idx)"
                      />
                      <v-btn
                        icon="mdi-delete"
                        size="x-small"
                        variant="text"
                        color="error"
                        density="compact"
                        :disabled="eFiling.isSubmitting.value"
                        @click="removePendingBO(idx)"
                      />
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-divider />
              <v-card-actions class="pa-2">
                <v-btn
                  v-if="!readOnly"
                  color="primary"
                  variant="flat"
                  block
                  :loading="eFiling.isSubmitting.value"
                  @click="handleSubmitEFiling"
                >
                  <v-icon start size="small">mdi-send</v-icon>
                  {{ t('startTask.Submit E-Filing') || 'Submit' }} ({{ pendingBOs.length }} BO)
                </v-btn>
                <div v-if="readOnly" class="text-center text-caption text-grey w-100">
                  <v-icon size="14" class="mr-1">mdi-eye</v-icon>
                  {{ t('startTask.Click to view details') || 'Click a BO to view details' }}
                </div>
              </v-card-actions>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <!-- Validation Errors -->
      <v-alert
        v-if="!readOnly && eFiling.validationErrors.value.length > 0"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="eFiling.validationErrors.value = []"
      >
        <div class="font-weight-bold mb-2">{{ t('startTask.Validation Errors') || 'Validation Errors' }}:</div>
        <div v-for="(err, index) in eFiling.validationErrors.value" :key="index"
          :class="err.startsWith('  -') ? 'text-body-2 pl-4 text-error' : 'font-weight-medium mt-1'"
          :style="err.startsWith('  -') ? 'opacity: 0.85' : ''"
        >{{ err }}</div>
      </v-alert>

      <!-- Completed: result -->
      <v-card
        v-if="readOnly && (eFilingResultData || eFiling.pdfDataBase64.value)"
        variant="tonal"
        color="success"
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text-success">{{ t('startTask.E-Filing Completed') || 'E-Filing Completed' }}</span>
        </div>

        <!-- First BO Summary -->
        <template v-if="eFilingResultData?.firstBO">
          <v-card variant="outlined" class="mb-3 pa-3">
            <div class="text-subtitle-2 font-weight-bold mb-2 d-flex align-center">
              <v-icon size="18" class="mr-1">mdi-shield-account</v-icon>
              {{ t('startTask.Registered Beneficial Owner') || 'Registered Beneficial Owner' }}
              <v-chip v-if="eFilingResultData.addedCount > 1" size="x-small" color="info" class="ml-2">
                +{{ eFilingResultData.addedCount - 1 }} {{ t('startTask.more') || 'more' }}
              </v-chip>
            </div>
            <v-table density="compact" class="text-body-2 bg-transparent">
              <tbody>
                <tr v-if="eFilingResultData.entityName || eFilingResultData.uniqueEntityNumber">
                  <td class="font-weight-medium text-no-wrap" style="width: 140px">{{ t('startTask.Entity') || 'Entity' }}</td>
                  <td>{{ eFilingResultData.entityName }} ({{ eFilingResultData.uniqueEntityNumber }})</td>
                </tr>
                <tr>
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.Name') || 'Name' }}</td>
                  <td>{{ eFilingResultData.firstBO.name || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.ID Type / No') || 'ID Type / No' }}</td>
                  <td>{{ getIdTypeLabel(eFilingResultData.firstBO.idType) }} / {{ eFilingResultData.firstBO.idNo || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.Citizenship') || 'Citizenship' }}</td>
                  <td>{{ getCitizenshipLabel(eFilingResultData.firstBO.citizenship) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.Types of BO') || 'Types of BO' }}</td>
                  <td>{{ getTypeBoLabel(eFilingResultData.firstBO.typeBo) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.Total Registered') || 'Total Registered' }}</td>
                  <td>{{ eFilingResultData.addedCount || 1 }} {{ t('startTask.Beneficial Owner(s)') || 'Beneficial Owner(s)' }}</td>
                </tr>
                <tr v-if="eFilingResultData.completedAt">
                  <td class="font-weight-medium text-no-wrap">{{ t('startTask.Completed At') || 'Completed At' }}</td>
                  <td>{{ formatDateTime(eFilingResultData.completedAt) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </template>

        <v-alert
          v-if="eFiling.warningMessage.value"
          type="warning"
          variant="tonal"
          class="mb-3"
          density="compact"
          border="start"
        >
          <div class="d-flex align-start gap-2">
            <v-icon size="16" color="warning">mdi-alert</v-icon>
            <div class="text-body-2">{{ eFiling.warningMessage.value }}</div>
          </div>
        </v-alert>

        <v-btn v-if="eFiling.pdfDataBase64.value" color="primary" variant="flat" @click="handleDownloadPdf">
          <v-icon start>mdi-file-pdf-box</v-icon>
          {{ t('startTask.Download Receipt') || 'Download Receipt (PDF)' }}
        </v-btn>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="secondary"
        :disabled="eFiling.isSubmitting.value"
        @click="emit('back')"
      >
        <v-icon start>mdi-arrow-left</v-icon>
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-spacer />

      <!-- Temp Save -->
      <v-btn
        v-if="!readOnly"
        variant="outlined"
        color="primary"
        :loading="eFiling.isSaving.value"
        :disabled="eFiling.isSubmitting.value"
        class="mr-2"
        @click="eFiling.handleTempSave(taskId, boFormRef, readOnly, { pendingBOs: pendingBOs })"
      >
        <v-icon start>mdi-content-save-outline</v-icon>
        {{ t('startTask.Temp Save') || 'Temp Save' }}
      </v-btn>
    </v-card-actions>

    <!-- Credential Select Dialog -->
    <EFilingCredentialDialog
      v-model="eFiling.showCredentialDialog.value"
      :mycoid-credentials="eFiling.mycoidCredentials.value"
      :selected-id="eFiling.selectedCredentialId.value"
      :is-loading="eFiling.isLoadingCredentials.value"
      :credential-label="eFiling.credentialTypeLabel.value"
      @select="eFiling.selectCredential"
      @confirm="handleCredentialConfirmed"
      @manage="eFiling.openManageCredential"
    />

    <!-- Credential Manage Modal -->
    <CredentialManageModal
      v-model="eFiling.showManageCredentialDialog.value"
      @credential-added="eFiling.handleCredentialAdded"
    />

    <!-- Confirm Dialog -->
    <EFilingConfirmDialog
      v-model="eFiling.showConfirmDialog.value"
      :credential="eFiling.selectedCredential.value"
      :show-test-mode="eFiling.canUseTestMode"
      :test-mode="eFiling.testMode.value"
      @update:test-mode="eFiling.testMode.value = $event"
      @confirm="handleConfirmSubmit"
    />

    <!-- Process Dialog -->
    <EFilingProcessDialog
      v-model="eFiling.showProcessDialog.value"
      :step="eFiling.submitStep.value"
      :success="eFiling.resultSuccess.value"
      :message="eFiling.resultMessage.value"
      :pdf-data-base64="eFiling.pdfDataBase64.value"
      :warning-message="eFiling.warningMessage.value"
      :success-title="eFiling.testMode.value ? 'Test Mode - Validation Completed' : (t('startTask.BO E-Filing Success') || 'Beneficial Owner E-Filing Completed!')"
      :fail-title="t('startTask.E-Filing Failed') || 'E-Filing Failed'"
      @close="handleProcessDialogCloseLocal"
      @download-pdf="handleDownloadPdf"
    >
      <template #result>
        <!-- Real submit: show first BO summary -->
        <div v-if="!eFiling.testMode.value && pendingBOs.length > 0" class="text-left mt-2">
          <v-card variant="outlined" class="pa-3 mb-2">
            <div class="text-subtitle-2 font-weight-bold mb-2 d-flex align-center">
              <v-icon size="18" class="mr-1">mdi-shield-account</v-icon>
              {{ pendingBOs[0]?.name || '-' }}
              <v-chip v-if="pendingBOs.length > 1" size="x-small" color="info" class="ml-2">
                +{{ pendingBOs.length - 1 }} more
              </v-chip>
            </div>
            <div class="text-body-2 d-flex flex-column gap-1">
              <div><span class="font-weight-medium">ID:</span> {{ getIdTypeLabel(pendingBOs[0]?.idType) }} / {{ pendingBOs[0]?.idNo || '-' }}</div>
              <div><span class="font-weight-medium">Citizenship:</span> {{ getCitizenshipLabel(pendingBOs[0]?.citizenship) }}</div>
              <div><span class="font-weight-medium">Types of BO:</span> {{ getTypeBoLabel(pendingBOs[0]?.typeBo) }}</div>
              <div v-if="uniqueEntityNumber"><span class="font-weight-medium">Entity:</span> {{ entityName }} ({{ uniqueEntityNumber }})</div>
            </div>
          </v-card>
        </div>

        <!-- Test mode: full data table -->
        <div v-if="eFiling.testMode.value" class="text-left mt-2">
          <div class="text-body-1 text-grey-darken-1 mb-3">
            Test mode - No API calls were made. Below is the data that would be submitted.
          </div>
          <v-divider class="mb-3" />
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon size="18" class="mr-1">mdi-clipboard-check-outline</v-icon>
            Beneficial Owners ({{ pendingBOs.length }})
          </div>
          <v-table density="compact" class="text-body-2">
            <thead><tr>
              <th>#</th><th>Name</th><th>ID No</th><th>BO Type</th><th>Types of BO</th><th>Joint</th><th>Criteria E %</th>
            </tr></thead>
            <tbody>
              <tr v-for="(bo, i) in pendingBOs" :key="i">
                <td>{{ i + 1 }}</td>
                <td>{{ bo.name || '-' }}</td>
                <td>{{ bo.idNo || '-' }}</td>
                <td>{{ bo.boType || '-' }}</td>
                <td>{{ bo.typeBo === 'C' ? 'CONTROL' : bo.typeBo || '-' }}</td>
                <td>
                  <v-icon v-if="bo.isJointArrangement === 'true' || bo.isJointArrangement === true" color="success" size="small">mdi-check-circle</v-icon>
                  <span v-else>No</span>
                </td>
                <td>{{ bo.criteriaEPercent || '-' }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Group Arrangement (if available) -->
          <template v-if="groupFormDataRef && groupFormDataRef.members?.length >= 2">
            <v-divider class="my-3" />
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon size="18" class="mr-1">mdi-account-group</v-icon>
              Group Arrangement: {{ groupFormDataRef.groupName || '-' }}
            </div>
            <v-table density="compact" class="text-body-2">
              <thead><tr>
                <th>#</th><th>Name</th><th>ID No</th><th>Voting Rights (%)</th>
              </tr></thead>
              <tbody>
                <tr v-for="(m, i) in groupFormDataRef.members" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ m.name || '-' }}</td>
                  <td>{{ m.idNo || '-' }}</td>
                  <td>{{ m.votingRights?.toFixed?.(4) || m.votingRights || '-' }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-right font-weight-bold">Total:</td>
                  <td :class="{ 'text-success': groupFormDataRef.totalVotingRights > 50 }">
                    {{ groupFormDataRef.totalVotingRights?.toFixed?.(4) || groupFormDataRef.totalVotingRights || '0' }}%
                  </td>
                </tr>
              </tfoot>
            </v-table>
          </template>
        </div>
      </template>
    </EFilingProcessDialog>

    <!-- Temp Save Dialog -->
    <EFilingTempSaveDialog
      v-model="eFiling.showTempSaveDialog.value"
      :success="eFiling.tempSaveSuccess.value"
      :message="eFiling.tempSaveMessage.value"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import BeneficialOwnerForm from './BeneficialOwnerForm.vue';
import BeneficialOwnerGroupForm from './BeneficialOwnerGroupForm.vue';
import { beneficialOwnerApi } from '../../api/beneficialOwnerApi';
import { updateEFilingComplete, saveEFilingData } from '@/features/startTask/api/taskApi';
import CredentialManageModal from '@/features/Entity_my/components/credentials/CredentialManageModal.vue';
import { useEFilingSubmit } from '../../composables/useEFilingSubmit';
import { downloadPdf } from '../../composables/usePdfDownload';
import EFilingCredentialDialog from '../shared/EFilingCredentialDialog.vue';
import EfilingWalkthroughButton from '../shared/EfilingWalkthroughButton.vue';
import EFilingConfirmDialog from '../shared/EFilingConfirmDialog.vue';
import EFilingProcessDialog from '../shared/EFilingProcessDialog.vue';
import EFilingTempSaveDialog from '../shared/EFilingTempSaveDialog.vue';
import { apiClient } from '@/shared/api/client';

const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  entityCd?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  entityCd: '',
  showBackButton: true,
  readOnly: false,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [message: string];
}>();

// EFiling composable
const eFiling = useEFilingSubmit({ formType: 'M32005' });

// ProcessDialog close → emit 'submit' to parent (EFilingPage → TaskListPage)
// Instead of router.push (which doesn't close the v-dialog modal)
const handleProcessDialogCloseLocal = () => {
  eFiling.showProcessDialog.value = false;
  eFiling.submitStep.value = 'idle';
  if (eFiling.isSubmitSuccess.value) {
    emit('submit', { addedCount: pendingBOs.value.length });
  }
};

// Entity → uniqueEntityNumber (SSM registration number) resolution
const uniqueEntityNumber = ref('');
const entityName = ref('');

const loadEntityInfo = async () => {
  if (!props.entityCd) return;
  try {
    const response = await apiClient.get('/startTask/common/entities');
    const data = (response as any)?.data || response;
    const entities = data?.entities || [];
    const entity = entities.find((e: any) => (e.entityCd || e.ENTITY_CD) === props.entityCd);
    if (entity) {
      uniqueEntityNumber.value = entity.regNoOld || entity.REG_NO_OLD || entity.regNo || entity.REG_NO || '';
      entityName.value = entity.companyName || entity.COMPANY_NAME || '';
      console.log('[BO-EFiling] Entity resolved:', { entityCd: props.entityCd, regNoOld: entity.regNoOld || entity.REG_NO_OLD, regNo: entity.regNo || entity.REG_NO, used: uniqueEntityNumber.value, name: entityName.value });
    }
  } catch (error) {
    console.error('[BO-EFiling] Failed to load entity info:', error);
  }
};

// Tab state
const activeTab = ref('addBO');

// Form refs
const boFormRef = ref<InstanceType<typeof BeneficialOwnerForm> | null>(null);
const bogFormRef = ref<InstanceType<typeof BeneficialOwnerGroupForm> | null>(null);

// Form data
const boFormDataRef = ref<any>(null);
const groupFormDataRef = ref<any>(null);

// E-Filing result (for read-only completion display)
const eFilingResultData = ref<any>(null);

// ===== Local pending BOs (saved locally, not yet submitted) =====
const pendingBOs = ref<any[]>([]);
const editingBOIndex = ref<number | null>(null);
const selectedBOIndex = ref<number>(0); // Read-only mode: currently viewed BO

// Label helpers for completion display
const ID_TYPE_LABELS: Record<string, string> = {
  '01': 'NRIC', '03': 'ARMY ID', '04': 'POLICE ID', '05': 'PASSPORT',
  '06': 'MyPR', '07': 'EXEMPTED', '08': 'MyKAS', '09': 'NATIONAL ID',
};
const CITIZENSHIP_LABELS: Record<string, string> = {
  'M': 'Malaysian', 'NM': 'Non-Malaysian', 'PR': 'Permanent Resident',
};
const TYPE_BO_LABELS: Record<string, string> = {
  'D': 'Direct Ownership', 'I': 'Indirect Ownership',
  'DI': 'Direct & Indirect', 'C': 'Control by Other Means',
};
const getIdTypeLabel = (code: string) => ID_TYPE_LABELS[code] || code || '-';
const getCitizenshipLabel = (code: string) => CITIZENSHIP_LABELS[code] || code || '-';
const getTypeBoLabel = (code: string) => TYPE_BO_LABELS[code] || code || '-';
const formatDateTime = (iso: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Convert pendingBOs to RegisteredBO format for GroupForm
const pendingBOsAsRegistered = computed(() =>
  pendingBOs.value.map((bo, idx) => ({
    personName: bo.name || '',
    identificationNumber: bo.idNo || '',
    identificationType: bo.idType || '',
    typesOfBO: bo.typeBo || '',
    boType: bo.boType || '',
    jointArrangement: bo.isJointArrangement === 'true' || bo.isJointArrangement === true || bo.jointArrangement === true,
  })),
);

// Eligible BO count: JA=true + typeBo=C (Group Arrangement 가능한 BO)
const eligibleBOCount = computed(() =>
  pendingBOsAsRegistered.value.filter(bo => {
    const isJA = bo.jointArrangement === true;
    const isControl = (bo.typesOfBO || '').toUpperCase().includes('CONTROL') || bo.typesOfBO === 'C';
    return isJA && isControl;
  }).length,
);

// BO별 Group 대상 여부 확인
const isEligibleForGroup = (bo: any): boolean => {
  const isJA = bo.isJointArrangement === 'true' || bo.isJointArrangement === true;
  const isControl = (bo.typeBo || '').toUpperCase().includes('CONTROL') || bo.typeBo === 'C';
  return isJA && isControl;
};

// ===== Lifecycle =====
onMounted(async () => {
  // Resolve entityCd → uniqueEntityNumber (SSM registration number)
  await loadEntityInfo();

  // Load saved pending BOs
  if (props.taskData?.pendingBOs) {
    pendingBOs.value = props.taskData.pendingBOs;
  }
  // Load form data
  if (props.taskData?.formData) {
    setTimeout(() => {
      if (boFormRef.value?.loadSavedData) {
        boFormRef.value.loadSavedData(props.taskData.formData);
      }
    }, 100);
  }
  // Read-only result
  if (props.readOnly && props.taskData?.eFilingResult) {
    const result = props.taskData.eFilingResult;
    eFiling.pdfDataBase64.value = result.pdfDataBase64 || null;
    eFiling.warningMessage.value = result.warningMessage || null;
    eFilingResultData.value = result;
    // Auto-load first BO into form
    if (pendingBOs.value.length > 0) {
      selectedBOIndex.value = 0;
      setTimeout(() => {
        boFormRef.value?.loadSavedData(pendingBOs.value[0]);
      }, 200);
    }
  }
});

watch(() => props.taskData, (newData) => {
  if (newData?.pendingBOs) {
    pendingBOs.value = newData.pendingBOs;
  }
  if (newData?.formData && boFormRef.value?.loadSavedData) {
    boFormRef.value.loadSavedData(newData.formData);
  }
}, { deep: true });

// ===== Handlers =====
const handleBoFormDataUpdate = (data: any) => {
  boFormDataRef.value = data;
};

const handleGroupFormDataUpdate = (data: any) => {
  groupFormDataRef.value = data;
};

const handleDownloadPdf = () => {
  if (eFiling.pdfDataBase64.value) {
    downloadPdf(eFiling.pdfDataBase64.value, 'BeneficialOwner_receipt');
  }
};

// ===== Add / Update to local list =====
const handleAddToList = async () => {
  if (!boFormRef.value) return;

  // Validate
  const isValid = await boFormRef.value.validate();
  if (!isValid) {
    eFiling.validationErrors.value = boFormRef.value.getValidationErrors?.() || ['Please fill in all required fields'];
    return;
  }
  eFiling.validationErrors.value = [];

  // Save to local list
  const formData = boFormRef.value.getFormData();
  if (!formData) return;

  if (editingBOIndex.value !== null) {
    // Update existing BO
    pendingBOs.value[editingBOIndex.value] = { ...formData };
    editingBOIndex.value = null;
  } else {
    // Add new BO
    pendingBOs.value.push({ ...formData });
  }

  // Auto temp save (before reset - form still has data for getFormData)
  if (props.taskId && !props.readOnly) {
    eFiling.handleTempSave(props.taskId, boFormRef.value, props.readOnly, { pendingBOs: pendingBOs.value });
  }

  // Reset form for next entry
  boFormRef.value.resetForm();

  // Scroll to top
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

// View BO in read-only mode: load into form
const viewPendingBO = (idx: number) => {
  if (!props.readOnly || !boFormRef.value) return;
  selectedBOIndex.value = idx;
  activeTab.value = 'addBO';
  setTimeout(() => {
    boFormRef.value?.loadSavedData(pendingBOs.value[idx]);
  }, 100);
};

// Edit pending BO: load into form
const editPendingBO = (idx: number) => {
  if (!boFormRef.value) return;
  editingBOIndex.value = idx;
  activeTab.value = 'addBO';
  // Load BO data into form
  setTimeout(() => {
    boFormRef.value?.loadSavedData(pendingBOs.value[idx]);
  }, 100);
};

// Cancel editing
const cancelEditBO = () => {
  editingBOIndex.value = null;
  boFormRef.value?.resetForm();
};

// Remove from local list
const removePendingBO = (idx: number) => {
  // If editing this BO, cancel edit first
  if (editingBOIndex.value === idx) {
    cancelEditBO();
  } else if (editingBOIndex.value !== null && editingBOIndex.value > idx) {
    editingBOIndex.value--;
  }
  pendingBOs.value.splice(idx, 1);
};

// ===== Test Data: 2 BO + Group Arrangement =====
const loadGroupTestData = () => {
  // Clear existing
  pendingBOs.value = [];

  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  // BO #1: ALI BIN AHMAD (Malaysian, NRIC, Joint Arrangement + Control)
  pendingBOs.value.push({
    userType: 'BO',
    changeDt: today,
    recordedDt: today,
    boType: 'I',
    category: 'I',
    idType: '01',
    idNo: '850101085001',
    dob: '1985-01-01',
    name: 'ALI BIN AHMAD',
    email: 'ali.ahmad@test.com',
    phoneNo: '0123456789',
    designation: 'DIRECTOR',
    citizenship: 'M',
    nationality: 'MALAYSIA',
    gender: 'M',
    race: 'M',
    isJointArrangement: 'true',
    isJointShareholder: 'false',
    typeBo: 'C',
    critboA: false, criteriaADirectPercent: '', criteriaAIndirectPercent: '',
    critboB: false, criteriaBDirectPercent: '', criteriaBIndirectPercent: '',
    critboC: false,
    critboD: false,
    critboE: true,
    criteriaEPercent: '30',
    critboF: false, criteriaFPercent: '',
    resIsLocal: true,
    resAdd1: 'NO. 10, JALAN BUKIT BINTANG',
    resAdd2: 'BUKIT BINTANG',
    resAdd3: '',
    resPostcode: '55100',
    resCity: 'KUALA LUMPUR',
    resState: 'WP KUALA LUMPUR',
    resCountry: '',
    resCityOverseas: '', resStateOverseas: '',
    bizSameAsRes: true,
    bizIsLocal: true,
    bizAdd1: '', bizAdd2: '', bizAdd3: '',
    bizPostcode: '', bizCity: '', bizState: '', bizCountry: '', bizCityOverseas: '', bizStateOverseas: '',
  });

  // BO #2: SITI BINTI RAHMAN (Malaysian, NRIC, Joint Arrangement + Control)
  pendingBOs.value.push({
    userType: 'BO',
    changeDt: today,
    recordedDt: today,
    boType: 'I',
    category: 'I',
    idType: '01',
    idNo: '900615085002',
    dob: '1990-06-15',
    name: 'SITI BINTI RAHMAN',
    email: 'siti.rahman@test.com',
    phoneNo: '0198765432',
    designation: 'SHAREHOLDER',
    citizenship: 'M',
    nationality: 'MALAYSIA',
    gender: 'F',
    race: 'M',
    isJointArrangement: 'true',
    isJointShareholder: 'false',
    typeBo: 'C',
    critboA: false, criteriaADirectPercent: '', criteriaAIndirectPercent: '',
    critboB: false, criteriaBDirectPercent: '', criteriaBIndirectPercent: '',
    critboC: false,
    critboD: false,
    critboE: true,
    criteriaEPercent: '25',
    critboF: false, criteriaFPercent: '',
    resIsLocal: true,
    resAdd1: 'NO. 22, JALAN AMPANG',
    resAdd2: 'AMPANG',
    resAdd3: '',
    resPostcode: '50450',
    resCity: 'KUALA LUMPUR',
    resState: 'WP KUALA LUMPUR',
    resCountry: '',
    resCityOverseas: '', resStateOverseas: '',
    bizSameAsRes: true,
    bizIsLocal: true,
    bizAdd1: '', bizAdd2: '', bizAdd3: '',
    bizPostcode: '', bizCity: '', bizState: '', bizCountry: '', bizCityOverseas: '', bizStateOverseas: '',
  });

  // Pre-fill Group Arrangement form
  setTimeout(() => {
    if (bogFormRef.value?.loadSavedData) {
      bogFormRef.value.loadSavedData({
        groupName: 'AHMAD FAMILY GROUP',
        members: [
          { name: 'ALI BIN AHMAD', idNo: '850101085001', idType: '01', votingRights: 30 },
          { name: 'SITI BINTI RAHMAN', idNo: '900615085002', idType: '01', votingRights: 25 },
        ],
      });
    }
  }, 200);

  // Switch to Group tab to show result
  activeTab.value = 'group';
};

// ===== Submit E-Filing =====
// Step 1: Validate → open credential dialog
const handleSubmitEFiling = async () => {
  if (pendingBOs.value.length === 0) return;

  await eFiling.loadCredentials();
  eFiling.showCredentialDialog.value = true;
};

// Step 2: Credential selected → open confirm dialog
const handleCredentialConfirmed = () => {
  eFiling.showCredentialDialog.value = false;
  eFiling.showConfirmDialog.value = true;
};

// Step 3: Confirmed → login → register all BOs → optional group → complete
const handleConfirmSubmit = async () => {
  if (props.readOnly) {
    eFiling.showConfirmDialog.value = false;
    return;
  }

  eFiling.startProcessing();

  // Test mode: skip all API calls, show validation result
  if (eFiling.testMode.value) {
    // Capture group data for display in result slot
    if (bogFormRef.value) {
      groupFormDataRef.value = bogFormRef.value.getFormData();
    }
    const hasGroup = groupFormDataRef.value?.members?.length >= 2;
    const groupMsg = hasGroup
      ? ` + Group "${groupFormDataRef.value.groupName}" (${groupFormDataRef.value.members.length} members, ${groupFormDataRef.value.totalVotingRights}% voting rights)`
      : '';
    eFiling.completeSuccess(
      `Test mode completed - ${pendingBOs.value.length} BO(s) validated${groupMsg}. No API calls were made.`,
    );
    eFiling.isSubmitSuccess.value = false; // Prevent navigation
    return;
  }

  try {
    // Validate uniqueEntityNumber
    if (!uniqueEntityNumber.value) {
      throw new Error('Unique Entity Number is not available. Please ensure the entity is properly selected.');
    }

    // Step 3-1: IDPRO Login
    const loginResponse: any = await beneficialOwnerApi.idproLogin(
      eFiling.selectedCredentialId.value || '',
    );

    if (!loginResponse.success || !loginResponse.session_id) {
      throw new Error(loginResponse.error || 'IDPRO Login failed. Please check your credential.');
    }

    const sessionId = loginResponse.session_id;

    // Step 3-2: Register each BO sequentially (same session)
    let lastPdfBase64: string | null = null;

    for (let i = 0; i < pendingBOs.value.length; i++) {
      const bo = pendingBOs.value[i];

      const response = await beneficialOwnerApi.appointBO({
        credentialId: eFiling.selectedCredentialId.value || '',
        sessionId: sessionId,
        uniqueEntityNumber: uniqueEntityNumber.value,
        boFormData: bo,
      });

      const apiData = (response as any)?.data || response;

      if (!apiData?.success) {
        throw new Error(
          apiData?.error || `Failed to register BO #${i + 1} (${bo.name || bo.idNo})`,
        );
      }

      // Keep last PDF
      if (apiData.pdf_data_base64 || apiData.pdfDataBase64) {
        lastPdfBase64 = apiData.pdf_data_base64 || apiData.pdfDataBase64;
      }
    }

    // Step 3-3: Refresh registered BO list from server (needed for group creation)
    let registeredBOList: any[] = [];
    if (pendingBOs.value.length >= 2) {
      try {
        const listResponse = await beneficialOwnerApi.getBOList(
          eFiling.selectedCredentialId.value || '',
          sessionId,
          uniqueEntityNumber.value,
        );
        const listData = (listResponse as any)?.data || listResponse;
        if (listData?.success && listData.beneficial_owners) {
          registeredBOList = listData.beneficial_owners;
          console.log(`[BeneficialOwnerEFiling] Registered BOs from server: ${registeredBOList.length}`);
        }
      } catch (listError) {
        console.warn('[BeneficialOwnerEFiling] Failed to refresh BO list:', listError);
      }
    }

    // Step 3-4: Group Arrangement (optional)
    let groupData = null;
    if (bogFormRef.value && pendingBOs.value.length >= 2) {
      const isGroupValid = await bogFormRef.value.validate();
      if (isGroupValid) {
        groupData = bogFormRef.value.getFormData();
      }
    }

    if (groupData && (groupData.members?.length >= 2 || groupData.selectedBOIds?.length >= 2)) {
      try {
        const groupResponse = await beneficialOwnerApi.appointBOGroup({
          credentialId: eFiling.selectedCredentialId.value || '',
          sessionId: sessionId,
          uniqueEntityNumber: uniqueEntityNumber.value,
          groupData: {
            ...groupData,
            registeredBOs: registeredBOList, // Server-side BO list for reference
          },
        });
        const groupApiData = (groupResponse as any)?.data || groupResponse;
        if (!groupApiData?.success) {
          eFiling.warningMessage.value = `BOs registered, but Group failed: ${groupApiData?.error || 'Unknown error'}`;
        }
      } catch (groupError: any) {
        eFiling.warningMessage.value = `BOs registered, but Group failed: ${groupError.message}`;
      }
    }

    // Step 3-5: Logout
    beneficialOwnerApi.idproLogout(sessionId).catch(() => {});

    // Step 3-6: Save task status FIRST (during processing phase, before showing success)
    if (props.taskId) {
      console.log('[BeneficialOwnerEFiling] Updating task status to completed, taskId:', props.taskId);
      try {
        const completeResult = await updateEFilingComplete(props.taskId);
        console.log('[BeneficialOwnerEFiling] updateEFilingComplete result:', completeResult);
      } catch (statusError: any) {
        const errorDetail = statusError?.response?.data || statusError?.message || statusError;
        console.error('[BeneficialOwnerEFiling] updateEFilingComplete failed:', errorDetail);
        eFiling.warningMessage.value = (eFiling.warningMessage.value || '') +
          '\nTask status update failed. Please refresh the page to verify the status.';
      }
    }
    // L_TASK_EFILING_DATA 저장 — taskId null 이면 standalone endpoint 자동 사용.
    try {
      const saveResult = await saveEFilingData(props.taskId ?? null, {
        taskTypeCd: 'M32005',
        entityCd: null,
        countryCd: 'MY',
        formData: {
          pendingBOs: pendingBOs.value,
          groupData: groupData,
          entityName: entityName.value,
          uniqueEntityNumber: uniqueEntityNumber.value,
        } as Record<string, unknown>,
        responseData: {
          addedCount: pendingBOs.value.length,
          firstBO: pendingBOs.value[0] || null,
        } as Record<string, unknown>,
        warningMessage: eFiling.warningMessage.value || null,
        receiptPdfBase64: lastPdfBase64 || null,
        receiptPdfFilename: 'BeneficialOwner_receipt.pdf',
        status: 'B36003',
        isSuccess: true,
        completedAt: new Date().toISOString(),
      });
      console.log('[BeneficialOwnerEFiling] saveEFilingData result:', saveResult);
    } catch (saveError: any) {
      console.error('[BeneficialOwnerEFiling] L_TASK_EFILING_DATA save failed:', saveError?.response?.data || saveError?.message || saveError);
    }

    // Step 3-7: Show success (after status update)
    eFiling.pdfDataBase64.value = lastPdfBase64;
    eFiling.completeSuccess(
      `E-Filing completed! ${pendingBOs.value.length} Beneficial Owner(s) registered.`,
    );

    // Note: emit('submit') is deferred to handleProcessDialogCloseLocal (when user clicks "Done")
  } catch (error: any) {
    eFiling.completeError(error, 'Failed to register Beneficial Owner');
    // ProcessDialog에서 에러 표시 → emit('error') 불필요 (중복 알림 방지)
  }
};
</script>

<style scoped>
.bo-sidebar {
  position: sticky;
  top: 80px;
}

.bo-sidebar-card {
  border-color: rgb(var(--v-theme-primary), 0.3);
}

.bo-list-item {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.08);
}

.bo-list-item:last-child {
  border-bottom: none;
}

.bo-list-item--selected {
  border-left: 3px solid rgb(var(--v-theme-primary));
}
</style>
